import axios from "axios";
import React from "react";
import { useParams } from "react-router-dom";

import { getUrlForBookings } from "../../../utils/confighelpers";

/**
 * Short payment link (2026-11-01), route /p/:code — public, no login.
 * Asks the backend where the link leads (GET bookings/payLink?code=…) and opens it (Stripe Checkout).
 * An expired or unknown link gets a message instead. Same look as PaymentReturnPage.
 */
export default function ShortPayLinkPage() {
  const { code = "" } = useParams();
  const [state, setState] = React.useState("loading"); // loading | expired | missing

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${getUrlForBookings()}payLink`, { params: { code } });
        const data = res?.data?.statusCode === 200 ? res.data.data : null;
        if (!alive) return;
        if (data?.url && /^https:\/\//i.test(data.url)) {
          window.location.replace(data.url);
          return;
        }
        setState(data?.expired ? "expired" : "missing");
      } catch (e) {
        if (alive) setState("missing");
      }
    })();
    return () => {
      alive = false;
    };
  }, [code]);

  const title = state === "expired" ? "This payment link has expired" : "This payment link doesn't work";
  const text =
    state === "expired"
      ? "Payment links work for 24 hours. Nothing was charged. Ask your club to send you a new link."
      : "Please check that you opened the whole link, or ask your club to send it again.";

  return (
    <main className="flex min-h-screen items-center justify-center bg-[#F7F4F8] px-4 py-10 font-[Mulish,sans-serif]">
      <div className="w-full max-w-md rounded-3xl bg-white p-7 text-center shadow-[0_20px_60px_rgba(72,27,58,0.12)] sm:p-9" role="status" aria-live="polite">
        {state === "loading" ? (
          <>
            <span className="mx-auto block h-12 w-12 animate-spin rounded-full border-4 border-[#EDE5F0] border-t-[#481B3A]" aria-hidden="true" />
            <h1 className="mt-5 text-xl font-black tracking-tight text-gray-900">Opening secure payment…</h1>
            <p className="mt-2 text-[15px] text-gray-600">One moment, you're being taken to the payment page.</p>
          </>
        ) : (
          <>
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-[#FFF4E5] text-[#7A4A00]">
              <svg viewBox="0 0 24 24" className="h-8 w-8" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 8v5" />
                <path d="M12 16h.01" />
                <circle cx="12" cy="12" r="9" />
              </svg>
            </span>
            <h1 className="mt-5 text-2xl font-black tracking-tight text-gray-900">{title}</h1>
            <p className="mt-2 text-[15px] text-gray-600">{text}</p>
            <p className="mt-4 text-xs text-gray-500">You can close this page.</p>
          </>
        )}
      </div>
    </main>
  );
}
